import * as React from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { makeStyles, mergeClasses, Skeleton, SkeletonItem, Text, tokens } from "@fluentui/react-components";
import { Project } from "../../models/ProjectsResponse";
import { NewformaRadioGroup, NewformaRadioSection } from "../_base/NewformaRadioGroup";
import { NewformaSpinner } from "../_base/Spinner";
import { useTheme } from "../../hooks/useTheme";

const MAX_VISIBLE_PROJECTS = 15;
const MIN_SERVER_SEARCH_LENGTH = 3;

const useStyles = makeStyles({
    itemHighlight: {
        backgroundColor: tokens.colorPaletteYellowBackground2,
    },
    projectPickerSection: {
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        overflowX: "hidden",
        scrollbarGutter: "stable",
    },
    loadingContainer: {
        flexGrow: "1",
        userSelect: "none",
        scrollbarGutter: "stable",
        display: "flex",
        flexDirection: "column",
        paddingBottom: tokens.spacingVerticalXS,
    },
    loadingRows: {
        display: "flex",
        flexDirection: "column",
        rowGap: "14px",
    },
    loadingItemContainer: {
        display: "flex",
        columnGap: tokens.spacingHorizontalS,
        alignItems: "center",
        height: "24px",
    },
    searchResultsTitle: {
        color: tokens.colorBrandForegroundLink,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        marginBottom: "5px",
    },
    statusRow: {
        flexGrow: "1",
        display: "flex",
        alignItems: "start",
        gap: "6px",
        marginTop: "8px",
    },
    noneFoundContent: {
        flexGrow: "1",
        textAlign: "center",
        paddingTop: "4em",
        display: "flex",
        flexDirection: "column",
    },
    noneFoundImage: {
        width: "60%",
        height: "auto",
        alignSelf: "center",
    },
    noneFoundTitle: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        paddingTop: "1em",
    },
    noneFoundSubtext: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        fontWeight: tokens.fontWeightRegular,
    },
    noneFoundSubtextSpaced: {
        paddingTop: "1em",
    },
});

export interface ProjectRadiosFluent9Props extends LocalizeContextProps {
    useGlobalProjects: boolean;
    isGlobalProjectSearchSupported: boolean;
    filterString: string;
    suggestedProjects: Project[];
    myProjects: Project[];
    globalProjects: Project[];
    serverSearchResults: Project[];
    isServerSearching: boolean;
    itemLabel: (item: Project) => string;
    onSelectionChanged: (item: Project | null) => void;
    isProjectFilterMatch: (project: Project, filterString: string) => boolean;
    isLoadingMyProjects: boolean;
    selectedItem: Project | null;
}

export interface RadioButtonListGroup {
    title: string;
    isVisibleIfEmpty: boolean;
    items: Project[];
}

export const getSortedProjects = (projects: Project[], itemLabel: (item: Project) => string): Project[] => {
    return [...projects].sort((left, right) =>
        itemLabel(left).localeCompare(itemLabel(right), undefined, { sensitivity: "base", numeric: true })
    );
};

export const getUniqueProjectsByNrn = (projects: Project[]): Project[] => {
    const projectMap = new Map<string, Project>();
    projects.forEach((project) => {
        if (!projectMap.has(project.nrn)) {
            projectMap.set(project.nrn, project);
        }
    });
    return Array.from(projectMap.values());
};

export const ProjectRadiosFluent9 = (props: ProjectRadiosFluent9Props): JSX.Element => {
    const styles = useStyles();
    const trimmedFilterLength = props.filterString.trim().length;

    const shouldShowKeepTypingHint =
        props.isGlobalProjectSearchSupported &&
        props.useGlobalProjects &&
        trimmedFilterLength >= 1 &&
        trimmedFilterLength < MIN_SERVER_SEARCH_LENGTH &&
        !props.isServerSearching;

    if (!props.filterString) {
        return (
            <ProjectRadiosList
                groups={[
                    {
                        title: props.translate("SEND_AND_FILE_NEW.SUGGESTED_PROJECTS") as string,
                        items: props.suggestedProjects,
                        isVisibleIfEmpty: false,
                    },
                    {
                        title: props.translate("SEND_AND_FILE_NEW.MY_PROJECTS") as string,
                        items: props.myProjects.slice(0, MAX_VISIBLE_PROJECTS),
                        isVisibleIfEmpty: true,
                    },
                ]}
                filter={props.filterString}
                onSelectionChanged={props.onSelectionChanged}
                itemLabel={props.itemLabel}
                isLoading={props.isLoadingMyProjects}
                selectedItem={props.selectedItem}
            />
        );
    }

    if (
        props.isGlobalProjectSearchSupported &&
        props.useGlobalProjects &&
        trimmedFilterLength >= MIN_SERVER_SEARCH_LENGTH
    ) {
        if (props.isServerSearching) {
            return (
                <div>
                    <Text className={styles.searchResultsTitle}>
                        {props.translate("SEND_AND_FILE_NEW.SEARCH_RESULTS")}
                    </Text>
                    <div data-testid="loading-status" className={styles.statusRow}>
                        <NewformaSpinner size="tiny" />
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                            {props.translate("SHARED.SUGGESTED_PROJECTS.SEARCHING_ALL_PROJECTS")}
                        </Text>
                    </div>
                </div>
            );
        }

        if (!props.serverSearchResults.length) {
            return <NoneFound useGlobalProjects={props.useGlobalProjects} />;
        }

        // My projects first (client-filtered), then server-matched my-projects the client missed, then global-only.
        // Sorting all server results together would let a global project appear above a my project.
        const uniqueServerResults = getUniqueProjectsByNrn(props.serverSearchResults);
        const myProjectNrns = new Set(props.myProjects.map((p) => p.nrn));
        const clientFilteredMyProjects = props.myProjects.filter((p) =>
            props.isProjectFilterMatch(p, props.filterString)
        );
        const clientFilteredNrns = new Set(clientFilteredMyProjects.map((p) => p.nrn));
        const serverMatchedMyProjects = uniqueServerResults.filter(
            (p) => myProjectNrns.has(p.nrn) && !clientFilteredNrns.has(p.nrn)
        );
        const globalOnlyResults = uniqueServerResults.filter((p) => !myProjectNrns.has(p.nrn));
        const sortedResults = [
            ...getSortedProjects(clientFilteredMyProjects, props.itemLabel),
            ...getSortedProjects(serverMatchedMyProjects, props.itemLabel),
            ...getSortedProjects(globalOnlyResults, props.itemLabel),
        ];

        return (
            <ProjectRadiosList
                groups={[
                    {
                        title: props.translate("SEND_AND_FILE_NEW.SEARCH_RESULTS") as string,
                        items: sortedResults.slice(0, MAX_VISIBLE_PROJECTS),
                        isVisibleIfEmpty: false,
                    },
                ]}
                filter={props.filterString}
                onSelectionChanged={props.onSelectionChanged}
                itemLabel={props.itemLabel}
                isLoading={props.isLoadingMyProjects}
                selectedItem={props.selectedItem}
            />
        );
    }

    if (props.isGlobalProjectSearchSupported) {
        const filteredSuggested = props.suggestedProjects.filter((project: Project) =>
            props.isProjectFilterMatch(project, props.filterString)
        );
        const filteredMyProjects = props.myProjects.filter((project: Project) =>
            props.isProjectFilterMatch(project, props.filterString)
        );
        const filteredItems = getSortedProjects(
            getUniqueProjectsByNrn(filteredSuggested.concat(filteredMyProjects)),
            props.itemLabel
        );

        if (!filteredItems.length) {
            return <NoneFound useGlobalProjects={props.useGlobalProjects} />;
        }

        return (
            <ProjectRadiosList
                groups={[
                    {
                        title: props.translate("SEND_AND_FILE_NEW.SEARCH_RESULTS") as string,
                        items: filteredItems.slice(0, MAX_VISIBLE_PROJECTS),
                        isVisibleIfEmpty: false,
                    },
                ]}
                filter={props.filterString}
                onSelectionChanged={props.onSelectionChanged}
                itemLabel={props.itemLabel}
                isLoading={props.isLoadingMyProjects}
                selectedItem={props.selectedItem}
                hintText={
                    shouldShowKeepTypingHint && filteredItems.length > 0
                        ? (props.translate("SHARED.SUGGESTED_PROJECTS.KEEP_TYPING_HINT") as string)
                        : undefined
                }
            />
        );
    }

    const arrayToSearch = props.useGlobalProjects ? props.globalProjects : props.myProjects;
    const filteredItems = getSortedProjects(
        getUniqueProjectsByNrn(
            arrayToSearch.filter((project: Project) => props.isProjectFilterMatch(project, props.filterString))
        ),
        props.itemLabel
    );

    if (!filteredItems.length) {
        return <NoneFound useGlobalProjects={props.useGlobalProjects} />;
    }

    return (
        <ProjectRadiosList
            groups={[
                {
                    title: props.translate("SEND_AND_FILE_NEW.SEARCH_RESULTS") as string,
                    items: filteredItems.slice(0, MAX_VISIBLE_PROJECTS),
                    isVisibleIfEmpty: false,
                },
            ]}
            filter={props.filterString}
            onSelectionChanged={props.onSelectionChanged}
            itemLabel={props.itemLabel}
            isLoading={props.isLoadingMyProjects}
            selectedItem={props.selectedItem}
            hintText={
                shouldShowKeepTypingHint && filteredItems.length > 0
                    ? (props.translate("SHARED.SUGGESTED_PROJECTS.KEEP_TYPING_HINT") as string)
                    : undefined
            }
        />
    );
};

export interface ProjectRadiosListProps {
    itemLabel: (item: Project) => string;
    onSelectionChanged?: (item: Project | null) => void;
    groups: RadioButtonListGroup[];
    isLoading: boolean;
    filter?: string;
    selectedItem: Project | null;
    autoSelectFromGroups?: boolean;
    hintText?: string;
}

export const ProjectRadiosList = (props: ProjectRadiosListProps): JSX.Element => {
    const styles = useStyles();
    const [lastClickedValue, setLastClickedValue] = React.useState<string | undefined>(undefined);

    const handleChange = React.useCallback(
        (item: Project | null) => {
            props.onSelectionChanged?.(item);
        },
        [props.onSelectionChanged]
    );

    const effectiveSelectedItem = React.useMemo(() => {
        if (props.autoSelectFromGroups && props.groups.length > 0 && props.selectedItem?.nrn) {
            return (
                props.groups
                    .flatMap((group) => group.items)
                    .find((project) => project.nrn === props.selectedItem?.nrn) ?? null
            );
        }
        return props.selectedItem;
    }, [props.autoSelectFromGroups, props.groups, props.selectedItem]);

    const renderItemName = React.useCallback(
        (itemName: string): JSX.Element | string => {
            const { filter } = props;
            if (!filter) {
                return itemName;
            }

            const ix = itemName.toLowerCase().indexOf(filter.toLowerCase());
            if (ix === -1) {
                return <span>{itemName}</span>;
            }
            const leftStr = itemName.slice(0, ix);
            const rx = ix + filter.length;
            const midStr = itemName.slice(ix, rx);
            const rightStr = itemName.slice(rx);

            return (
                <>
                    {leftStr}
                    <span className={styles.itemHighlight}>{midStr}</span>
                    {rightStr}
                </>
            );
        },
        [props, styles.itemHighlight]
    );

    if (props.isLoading) {
        return <ProjectRadiosLoadingSkeleton />;
    }

    const itemByKey = new Map<string, Project>();
    let selectedGroupValue: string | undefined;
    let firstMatchValue: string | undefined;
    const sections: NewformaRadioSection[] = props.groups
        .map((group: RadioButtonListGroup, groupIndex) => {
            const options = group.items.map((item: Project) => {
                const key = `rbGroup${groupIndex}-${item.nrn}`;
                itemByKey.set(key, item);
                if (!firstMatchValue && effectiveSelectedItem?.nrn === item.nrn) {
                    firstMatchValue = key;
                }
                return {
                    value: key,
                    label: renderItemName(props.itemLabel(item)),
                };
            });

            return {
                key: `rbGroup${groupIndex}`,
                title: group.title ?? "",
                options,
            };
        })
        .filter((section, groupIndex) =>
            props.groups[groupIndex].isVisibleIfEmpty ? true : section.options.length > 0
        );

    if (effectiveSelectedItem?.nrn) {
        selectedGroupValue =
            lastClickedValue != null && itemByKey.get(lastClickedValue)?.nrn === effectiveSelectedItem?.nrn
                ? lastClickedValue
                : firstMatchValue;
    } else {
        selectedGroupValue = "";
    }

    return (
        <div data-testid="project-picker-section" className={styles.projectPickerSection}>
            <NewformaRadioGroup
                sections={sections}
                value={selectedGroupValue}
                onValueChange={(value) => {
                    setLastClickedValue(value);
                    handleChange(itemByKey.get(value) ?? null);
                }}
                hintText={props.hintText}
            />
        </div>
    );
};

interface NoneFoundProps extends LocalizeContextProps {
    useGlobalProjects: boolean;
}

export const NoneFound = withLocalize(
    (props: NoneFoundProps): JSX.Element => {
        const styles = useStyles();
        const { isDarkMode } = useTheme();
        return (
            <div data-testid="none-found" className={styles.noneFoundContent}>
                <img
                    src={isDarkMode ? "assets/NF_No_Results_Dark.svg" : "assets/NF_No_Results_Light.svg"}
                    className={styles.noneFoundImage}
                    alt="Newforma No Results"
                />
                <div className={styles.noneFoundTitle}>{props.translate("SEND_AND_FILE_NEW.NO_PROJECTS_FOUND")}</div>
                {!props.useGlobalProjects && (
                    <div className={mergeClasses(styles.noneFoundSubtext, styles.noneFoundSubtextSpaced)}>
                        {props.translate("SEND_AND_FILE_NEW.ONLY_MYPROJECTS_AVAILABLE")}
                    </div>
                )}
                <div
                    className={mergeClasses(
                        styles.noneFoundSubtext,
                        props.useGlobalProjects ? styles.noneFoundSubtextSpaced : undefined
                    )}
                >
                    {props.translate("SEND_AND_FILE_NEW.CHECK_SPELLING")}
                </div>
            </div>
        );
    }
);

export const ProjectRadiosLoadingSkeleton = (): JSX.Element => {
    const styles = useStyles();

    return (
        <div className={styles.loadingContainer}>
            <Skeleton>
                <SkeletonItem
                    size={20}
                    style={{
                        width: "96px",
                        marginBottom: tokens.spacingVerticalL,
                        borderRadius: tokens.borderRadiusSmall,
                    }}
                />
            </Skeleton>
            <div className={styles.loadingRows}>
                {Array.from({ length: 4 }, (_, i) => (
                    <div key={i} className={styles.loadingItemContainer}>
                        <Skeleton>
                            <SkeletonItem shape="circle" size={16} />
                        </Skeleton>
                        <Skeleton style={{ flexGrow: 1 }}>
                            <SkeletonItem size={24} style={{ borderRadius: tokens.borderRadiusSmall }} />
                        </Skeleton>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default withLocalize(ProjectRadiosFluent9);
